import { useState, useCallback } from "react"
import { Button } from "~/react/ui/button"
import DiscoverFilters from "./discover_filters"
import ProactiveAiPrompt from "./proactive-ai-prompt"
import {
  DiscoverResults,
  DiscoverResultsTable,
  DiscoverResultsPagination,
  NoResults,
  UpgradePrompt,
  LoadingState,
} from "./discover_results"
import type {
  DiscoverFilters as DiscoverFiltersType,
  DiscoverPageProps,
} from "~/react/types/discover"
import { useDiscoverFilters } from "~/react/hooks/useDiscoverFilters"
import { useDiscoverResults } from "~/react/hooks/useDiscoverResults"
import { discover_ai_filters_path } from "~/lib/routes"
import { getCsrfToken } from "~/lib/utils"
import {
  PageHeader,
  PageHeaderActions,
  PageHeaderTitle,
} from "../ui/page-header"

const t = (key: string, options?: Record<string, unknown>) =>
  window.i18n.t(key, options)

export interface DiscoverProps {
  filterProps: DiscoverPageProps
}

export default function Discover({ filterProps }: DiscoverProps) {
  const [chatId, setChatId] = useState<number | null>(null)
  const [isAiLoading, setIsAiLoading] = useState(false)
  const [currentOffset, setCurrentOffset] = useState(
    filterProps.pagination?.offset || 0,
  )
  const [currentPageSize, setCurrentPageSize] = useState(
    filterProps.pagination?.pageSize || 20,
  )

  const {
    results: initialResults,
    paths,
    hasFilters: serverHasFilters,
    entitlement,
  } = filterProps

  // Lift filter state to this component so AI prompt can update it
  const {
    filters,
    setFilters,
    updateFilter,
    clearAllFilters: baseClearAllFilters,
    hasFilters,
  } = useDiscoverFilters(filterProps.currentFilters)

  // Use the results hook for API-based fetching
  const {
    results,
    isLoading: isResultsLoading,
    hasFilters: resultsHasFilters,
    fetchResults,
    clearResults,
  } = useDiscoverResults({
    initialResults,
    initialHasFilters: serverHasFilters,
  })

  // Combined loading state
  const isLoading = isAiLoading || isResultsLoading

  // Wrap clearAllFilters to also reset chat context and clear results
  const clearAllFilters = useCallback(() => {
    baseClearAllFilters()
    setChatId(null)
    setCurrentOffset(0)
    clearResults()
  }, [baseClearAllFilters, clearResults])

  // Handle filter changes from DiscoverFilters component
  const handleFiltersChange = useCallback(
    (newFilters: DiscoverFiltersType) => {
      setCurrentOffset(0) // Reset to first page when filters change
      fetchResults(newFilters)
    },
    [fetchResults],
  )

  // Handle pagination
  const handlePageChange = useCallback(
    (newOffset: number) => {
      setCurrentOffset(newOffset)
      fetchResults(filters, newOffset)
    },
    [fetchResults, filters],
  )

  // Handle page size change
  const handlePageSizeChange = useCallback(
    (newPageSize: number) => {
      setCurrentPageSize(newPageSize)
      setCurrentOffset(0) // Reset to first page when page size changes
      fetchResults(filters, 0)
    },
    [fetchResults, filters],
  )

  const handleAiPromptSubmit = useCallback(
    async (query: string) => {
      setIsAiLoading(true)
      try {
        const response = await fetch(discover_ai_filters_path(), {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-CSRF-Token": getCsrfToken(),
          },
          body: JSON.stringify({ query, chat_id: chatId }),
        })

        if (!response.ok) {
          console.error("AI filter extraction failed:", response.status)
          return
        }

        const data = await response.json()
        setChatId(data.chat_id)

        // Update filters state with AI-extracted filters
        // This will trigger the auto-apply in DiscoverFilters
        const newFilters: DiscoverFiltersType = {
          ...data.filters,
          last_ai_query: query,
        }
        setFilters(newFilters)
      } catch (error) {
        console.error("AI filter extraction failed:", error)
      } finally {
        setIsAiLoading(false)
      }
    },
    [chatId, setFilters],
  )

  // Determine what to render in the content area
  const renderContent = () => {
    // No filters applied - show AI prompt (let it handle its own loading state)
    if (!resultsHasFilters) {
      return (
        <div className="board-box">
          <div className="discover-content">
            <span className="discover-content__title">
              {t("js.discover.title")}
            </span>
            <ProactiveAiPrompt onSubmit={handleAiPromptSubmit} />
            <span className="discover-content__caption">
              {t("js.discover.caption")}{" "}
              <Button
                asChild
                variant="linkGrey"
                size="sm"
                className="discover-content__caption-link"
              >
                <a href="/pricing">{t("js.discover.caption_link")}</a>
              </Button>
            </span>
          </div>
        </div>
      )
    }

    // Loading results after filters are applied
    if (isLoading) {
      return <LoadingState />
    }

    // Filters applied but no results
    if (!results || results.domains.length === 0) {
      return <NoResults />
    }

    // We have results - render them
    return (
      <div className="board-box discover-results-container">
        <PageHeader className="mb-0">
          <PageHeaderTitle>
            {t("js.discover.index.results_match_your_filters", {
              count: results.numberOfDomains,
              results: results.numberOfDomains.toLocaleString(),
            })}
          </PageHeaderTitle>
          <PageHeaderActions>
            <Button size="sm">
              {t("js.discover.index.save_all_companies")}
            </Button>
            <Button variant="primary" size="sm">
              {t("js.discover.index.find_all_people", {
                count: results.numberOfEmailAddresses,
                results: results.numberOfEmailAddresses.toLocaleString(),
              })}
            </Button>
          </PageHeaderActions>
        </PageHeader>
        <DiscoverResults>
          <DiscoverResultsTable
            domains={results.domains}
            domainsWithRecentFundings={results.domainsWithRecentFundings}
            domainsCurrentlyHiring={results.domainsCurrentlyHiring}
            discoversPath={paths?.discovers || "/discover"}
          />
          {entitlement.allowDiscoverPaging ? (
            <DiscoverResultsPagination
              pageSize={currentPageSize}
              offset={currentOffset}
              hasMoreDomains={results.hasMoreDomains}
              hasDomains={results.domains.length > 0}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
            />
          ) : (
            results.hasMoreDomains &&
            paths && (
              <UpgradePrompt
                remainingCount={
                  results.numberOfDomains - results.domains.length
                }
                paths={paths}
              />
            )
          )}
        </DiscoverResults>
      </div>
    )
  }

  return (
    <main className="dashboard-main">
      <div className="dashboard-main__filters discover-filters">
        <DiscoverFilters
          {...filterProps}
          filters={filters}
          updateFilter={updateFilter}
          clearAllFilters={clearAllFilters}
          hasFilters={hasFilters}
          showInlinePrompt={hasFilters}
          onAiPromptSubmit={handleAiPromptSubmit}
          onFiltersChange={handleFiltersChange}
        />
      </div>
      <div className="dashboard-main__content">{renderContent()}</div>
    </main>
  )
}
